<template>
  <div
    v-if="filterPermission"
    class="filters"
  >
    <form
      class="mb-3"
      :class="{ 'px-3 mt-3': !smAndUp }"
    >
      <v-layout
        justify-space-between
        row
        fill-height
        wrap
      >
        <v-flex
          sm12
          md3
        >
          <v-combobox
            v-model="filter.searchInput"
            v-validate="validationRules.get('search_field')"
            allow-overflow
            :loading="isLoadingSearchBar"
            hide-no-data
            :items="getSuggestResult"
            :search-input.sync="filter.searchInput"
            class="sz_grafit--text mb-0 search-box"
            :label="$t('label.search')"
            :placeholder="$t('label.search_ph')"
            no-filter
            :error-messages="errors.collect('search_bar')"
            :data-vv-as="' '"
            data-vv-name="search_bar"
            @keyup.enter="applyFilter"
          >
            <template slot="append">
              <div
                v-if="filter.searchInput"
                class="clickable ma-2"
                @click="filter.searchInput = null"
              >
                <BaseIcon><CloseIcon /></BaseIcon>
              </div>
              <div
                class="clickable ma-2"
                @click="applyFilter"
              >
                <BaseIcon><SearchIcon /></BaseIcon>
              </div>
            </template>
            <div
              slot="item"
              slot-scope="prop"
              class="search-results__item"
            >
              <v-layout column>
                <v-flex>
                  <h4 class="display-1 display-1_dark">
                    {{ $t('search.suggest_headers.' + prop.item[0]) }} ({{ prop.item[1].amount }})
                  </h4>
                </v-flex>
                <v-flex
                  v-for="(item, index) in prop.item[1].data"
                  :key="index"
                  class="pl-3 pt-2"
                >
                  <router-link
                    class="small dark d-block py-1"
                    :to="{ name: 'family-card', params: { id: item.familyEditionId } }"
                  >
                    {{ item.text }}
                  </router-link>
                </v-flex>
              </v-layout>
            </div>
            <template slot="append-item">
              <v-divider class="my-2" />
              <span
                v-if="getSuggestResult.length === 0"
                class="px-3 small d-inline-flex"
              >
                {{ $t('label.no_search_results') }}
              </span>
              <a
                v-else
                class="px-3 small d-inline-flex"
                @click="applyFilter"
              >
                {{ $t('label.show_search_results') }} {{ searchedQuery.query }} ({{
                  searchedQuery.amount
                }})
              </a>
            </template>
          </v-combobox>
          <div class="text-xs-right">
            <v-tooltip
              v-model="searchTooltipVisibility"
              bottom
              max-width="300"
            >
              <template #activator="{ on }">
                <a
                  class="sz_violet--text search-help"
                  v-on="on"
                >
                  {{ $t('filters.help') }}
                </a>
              </template>
              <span class="f-14">{{ $t('message.search_hint') }}</span>
            </v-tooltip>
          </div>
          <v-checkbox
            v-model="filter.user_volunteered"
            :label="$t('label.show_my_families')"
            type="checkbox"
          />
        </v-flex>
        <v-flex
          v-if="filterGroups"
          md6
          sm6
        >
          <v-layout
            fill-height
            wrap
          >
            <v-spacer />
            <v-flex
              xs12
              sm12
              md6
            >
              <div
                v-for="(item, index) in filterGroupLeft"
                :key="index"
              >
                <component
                  :is="item?.componentReference"
                  v-if="item?.componentReference"
                  v-model="filter[item?.name]"
                  v-bind="item"
                />
              </div>
            </v-flex>
            <v-flex
              xs12
              sm12
              md6
            >
              <div
                v-for="(item, index) in filterGroupRight"
                :key="index"
              >
                <component
                  :is="item?.componentReference"
                  v-if="item?.componentReference"
                  v-model="filter[item?.name]"
                  v-bind="item"
                />
              </div>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex
          md3
          sm6
        >
          <v-layout
            fill-height
            column
            :align-center="$vuetify.breakpoint.smAndDown"
            :align-start="$vuetify.breakpoint.mdAndUp"
          >
            <v-btn
              color="sz_violet"
              class="mr-0 ml-1 mt-3 text-none border-none"
              flat
              :outline="isFilterEmpty && isCurrentFilterEmpty"
              :disabled="isFilterEmpty && isCurrentFilterEmpty"
              @click="applyFilter"
            >
              <BaseIcon>
                <FiltersIcon />
              </BaseIcon>
              {{ $t('filters.filter') }}
            </v-btn>
            <v-btn
              color="sz_violet"
              class="mr-0 ml-1 mt-3 text-none border-none"
              flat
              :outline="isCurrentFilterEmpty"
              :disabled="isCurrentFilterEmpty"
              @click="clearFilter"
            >
              {{ $t('buttons.reset_filters') }}
            </v-btn>
          </v-layout>
        </v-flex>
      </v-layout>
    </form>
    <v-divider v-show="smAndUp" />
  </div>
</template>

<script>
import BaseIcon from '@/icons/BaseIcon';
import SearchIcon from '@/icons/SearchIcon';
import CloseIcon from '@/icons/CloseIcon';
import FiltersIcon from '@/icons/FiltersIcon';
import FilterItem from '@/components/widgets/dataTable/FilterItem';
import permissions from '@/constants/permissions';

import validationRules from '@/constants/validationRules';
import debounce from 'lodash.debounce';
import search from '@/mixins/search';
import { pendingFamiliesListFilters, qualifiedFamiliesListFilters } from '@/utils/filters';
import isArray from 'lodash.isarray';
import isBoolean from 'lodash.isboolean';

export default {
    name: 'FiltersBar',
    components: { SearchIcon, BaseIcon, CloseIcon, FiltersIcon, FilterItem },
    mixins: [search],
    props: {
        filtersValuesId: {
            type: Number
        }
    },
    data () {
        return {
            validationRules,
            permissions,
            searchedQuery: { query: '', amount: 0 },
            searchTooltipVisibility: false,
            appliedFilter: false,
            filterGroups: [],
            filterGroupLeft: [],
            filterGroupRight: []
        };
    },
    computed: {
        filter () {
            return this.$store.getters['search/getFilter'];
        },
        isMyFamiliesList () {
            return this.filtersValuesId === 2;
        },
        filterPermission () {
            return this.$store.getters['auth/getPermissions'].includes(this.permissions.USE_SEARCH_ENGINE);
        },
        smAndUp () {
            return this.$vuetify.breakpoint.smAndUp;
        },
        isLoadingSearchBar () {
            return this.$store.getters['search/getSearchSuggestLoader'];
        },
        getSuggestResult () {
            return this.$store.getters['search/getQualifiedSuggestResult'];
        },
        filterResources () {
            return this.$store.getters['search/getFilterResources'] || {};
        }
    },
    watch: {
        filterGroups: {
            handler (val) {
                this.filterGroupLeft = val.filter((item) => item?.column === 'left').slice();
                this.filterGroupRight = val.filter((item) => item?.column === 'right').slice();
            }
        },
        'filter.searchInput': {
            handler () {
                this.appliedFilter = false;
                this.searchSuggest();
            }
        },
        'filter.user_volunteered': {
            handler () {
                this.applyFilter();
            }
        },
        'filter.provinces': {
            handler (val) {
                if (val.length === 0) {
                    this.filter.locations = [];
                }
                const locations = this.filterGroups.find((el) => el.name === 'locations');
                if (
                    isArray(this.filterResources?.wioStructLocationIds) &&
          locations?.aggregated_filter_values &&
          isBoolean(locations?.loader)
                ) {
                    locations.loader = true;
                    locations.aggregated_filter_values = this.filterResources.wioStructLocationIds
                        .filter((v) => val.includes(Number(v.province_id)))
                        .slice();
                    if (locations.aggregated_filter_values.length === 1) {
                        this.filter.locations = [locations.aggregated_filter_values[0]?.id];
                    }
                    locations.loader = false;
                }
            }
        },
        'filter.locations': {
            async handler (val) {
                const volunteers = this.filterGroups.find((el) => el.name === 'volunteers');
                volunteers.showInfoAlert = val.length === 0;
                if (val.length === 0) {
                    this.filter.volunteers = [];
                    return;
                }
                await this.$store.dispatch('search/getVolunteersByRegion', val).then((data) => {
                    if (volunteers) {
                        volunteers.loader = true;
                        volunteers.aggregated_filter_values = [];
                        val.forEach((el) => {
                            volunteers.aggregated_filter_values.push(...data[el]);
                        });
                        volunteers.loader = false;
                    }
                });
            }
        },
        'filter.status': {
            handler (val) {
                if (val.length === 0) {
                    this.filter.advanced_status = [];
                }
                const advancedStatuses = this.filterGroups.find((el) => el.name === 'advanced_status');
                if (
                    isArray(this.filterResources?.advancedStatus) &&
          advancedStatuses?.aggregated_filter_values &&
          isBoolean(advancedStatuses?.loader)
                ) {
                    advancedStatuses.loader = true;
                    advancedStatuses.aggregated_filter_values = this.filterResources.advancedStatus
                        .filter((v) => val.includes(Number(v?.parent?.family_edition_status_id)))
                        .slice();
                    advancedStatuses.loader = false;
                }
            }
        }
    },
    methods: {
        applyFilter () {
            this.appliedFilter = true;
            this.getFilter();
        },
        getFilter () {
            const filters = this.getFiltersWithValue();

            if (this.isMyFamiliesList) {
                this.$store.dispatch('familyData/getMyFamilies', filters);
            } else {
                this.$store.dispatch('familyData/getNoVolunteersFamilies', filters);
            }
        },
        clearFilter () {
            this.$store.commit('search/clearFilter');
            this.applyFilter();
        },
        searchSuggest: debounce(function () {
            this.$store.commit('search/setQualifiedSuggestResult', []);
            this.$validator.validate('search_bar').then((result) => {
                if (result) {
                    this.$store
                        .dispatch('search/searchSuggest', {
                            searchInput: this.filter.searchInput,
                            isQualified: this.$route.name === 'families-list'
                        })
                        .then((searchedQueryString) => {
                            this.searchedQuery = searchedQueryString;
                            document.querySelectorAll('.search-results__item').forEach((item) => {
                                item.parentElement.style = 'height: auto; cursor: default';
                            });
                        });
                }
            });
        }, 600),
        selectFirstValue () {
            const provinces = this.filterGroups.find((el) => el.name === 'provinces');
            const aggregatedFilterValues = provinces && provinces.aggregated_filter_values;
            if (aggregatedFilterValues && aggregatedFilterValues.length === 1) {
                this.filter.provinces = [aggregatedFilterValues[0]?.provinceId];
            }
        }
    },
    async mounted () {
        if (this.filterPermission) {
            await this.$store.dispatch('search/getFilterResources', this.filtersValuesId);
            this.filterGroups = this.isMyFamiliesList
                ? qualifiedFamiliesListFilters(this.filterResources)
                : pendingFamiliesListFilters(this.filterResources);
            this.selectFirstValue();
        }
    },
    created () {
        this.getFilter();
    },
    beforeDestroy () {
        if (this.appliedFilter === false) {
            this.filter.searchInput = null;
        }
    }
};
</script>

<style lang="scss" scoped>
.theme--light {
  .search-box {
    & .v-input__slot {
      margin-bottom: 5px;
    }
  }
  .search-help {
    font-size: 14px;
    font-weight: normal;
  }
  .border-none {
    border: 0 !important;
  }
}
.search-results__item {
  width: 100%;
}
</style>
